import React, { useEffect, useRef, useState } from 'react';

type Props = {
  src: string;
  alt?: string;
  className?: string;
  showHint?: boolean;
};

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 0.25;

/**
 * Image viewer with:
 * - Wheel zoom anchored at cursor position (stays focused on the point you're inspecting)
 * - Click-drag to pan when zoomed in
 * - Double-click to reset to 1x
 *
 * Fills the parent container, so the caller decides the viewport size
 * (popup vs inline preview).
 */
const ZoomableImage: React.FC<Props> = ({ src, alt = '', className, showHint = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  // Mirror state into a ref so the native wheel listener (which is bound
  // only once on mount) always reads the latest values without rebinding.
  // Previously the effect had [scale, tx, ty] deps and rebound on every
  // change, causing a narrow window where the listener was detached while
  // React was flushing — fast scroll wheels could lose events and make
  // the zoom feel stuck.
  const stateRef = useRef({ scale: 1, tx: 0, ty: 0 });
  stateRef.current = { scale, tx, ty };

  // Reset when src changes (new image shown).
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [src]);

  // Native wheel listener (non-passive) to allow preventDefault.
  // Bind exactly once on mount — state is read via stateRef.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;

      const { scale: s, tx: x, ty: y } = stateRef.current;
      const direction = e.deltaY < 0 ? 1 : -1;
      const nextScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, s + direction * ZOOM_STEP),
      );
      if (nextScale === s) return;

      if (nextScale === 1) {
        setScale(1);
        setTx(0);
        setTy(0);
        return;
      }

      // Keep the image point under the cursor fixed while zooming.
      const ratio = nextScale / s;
      setScale(nextScale);
      setTx(cx - (cx - x) * ratio);
      setTy(cy - (cy - y) * ratio);
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (scale === 1) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, tx, ty };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragging || !dragStart.current) return;
    setTx(dragStart.current.tx + (e.clientX - dragStart.current.x));
    setTy(dragStart.current.ty + (e.clientY - dragStart.current.y));
  };

  const stopDrag = () => {
    setDragging(false);
    dragStart.current = null;
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const cursor = scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in';

  return (
    <div
      ref={containerRef}
      className="tt-zoomable-img"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onDoubleClick={handleDoubleClick}
      style={{ cursor }}
    >
      <img
        src={src}
        alt={alt}
        className={className}
        draggable={false}
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transition: dragging ? 'none' : 'transform 0.08s ease-out',
        }}
      />
      {scale > 1 && (
        <span className="tt-zoomable-img__badge">{Math.round(scale * 100)}%</span>
      )}
      {showHint && scale === 1 && (
        <div className="tt-zoomable-img__hint">Lăn chuột để zoom tại con trỏ</div>
      )}
    </div>
  );
};

/**
 * Stop re-rendering when only unrelated props change — ZoomableImage has
 * non-trivial internal state (scale, pan offset) and setting up the native
 * wheel listener on every render stutters the image visually. The parent
 * re-renders on every keystroke in the info panel, so shielding this
 * component with React.memo keeps the image steady while the operator
 * types. Only `src`, `alt`, `className`, `showHint` matter — all primitive.
 */
export default React.memo(ZoomableImage);
